import { Actions } from 'vuex-smart-module';
import {
  DocsTemplates,
  PartnersStatuses,
  PartnersStatusesTranslate,
  ResponseStatuses,
} from '@/enums';
import { AxiosResponse } from 'axios';
import { DocsTemplatesService } from '@/services/documents';
import { Filters, FiltersPagination } from '@/types';
import OrganizationState from './state';
import OrganizationGetters from './getters';
import OrganizationMutations from './mutations';
import { OrganizationsService } from '../services';
import { DeliveryAddress, ModelContractsFilter, Organizations } from '../types';

const orgzService = new OrganizationsService();

export default class OrganizationActions extends Actions<
  OrganizationState,
  OrganizationGetters,
  OrganizationMutations,
  OrganizationActions
> {
  set_user_organization(org: Organizations.UserOrganization) {
    this.mutations.SET_USER_ORGANIZATION(org);
  }

  async get_organizations() {
    try {
      const { data } = await orgzService.get_organizations();
      const {
        address = '',
        name_company = '',
        partner_code = '',
        sigma_partner_code = '',
        frontol_partner_code = '',
        telephone = '',
        website = '',
        status = {
          slug: PartnersStatuses.PARTNER,
          name: PartnersStatusesTranslate[PartnersStatuses.PARTNER],
        },
        is_resident,
        company_manager,
        is_cert,
        manager_dpp = '',
        manager_dpp_email = '',
        company_roles = '',
        api_token = '',
        api_token_hidden = '',
        has_accredited_models,
        external_api_webhook_url,
        can_create_external_api_orders,
      } = data;

      const user_org: Organizations.UserOrganization = {
        address,
        name_company,
        partner_code,
        sigma_partner_code,
        frontol_partner_code,
        telephone,
        website,
        status,
        is_resident,
        company_manager,
        manager_dpp,
        manager_dpp_email,
        is_cert,
        company_roles,
        api_token,
        api_token_hidden,
        has_accredited_models,
        external_api_webhook_url,
        can_create_external_api_orders,
      };

      this.dispatch('set_user_organization', user_org);

      this.mutations.SET_ORGANIZATIONS(data.organizations);
      this.mutations.SET_ORGANIZATIONS_WITH_ASC_STATUS(data.organizations);
      this.mutations.SET_CITIES(data.cities);
    } catch (error) {
      console.error(error);
    }
  }

  async upd_organization(org: Partial<Organizations.Organization>) {
    try {
      const code = await orgzService.upd_organization(org);

      this.mutations.SET_CURRENT_ORGANIZATION((org as Organizations.Organization));
      return code;
    } catch (error) {
      console.error(error);
      return (error as AxiosResponse).status;
    }
  }

  async get_organization_by_id(id: string | number) {
    try {
      const { data } = await orgzService.get_organization_by_id(id);

      this.mutations.SET_CURRENT_ORGANIZATION(data);
    } catch (error) {
      console.log(error);

      return (error as AxiosResponse).data.code;
    }
  }

  async get_delivery_addresses(id: string | number) {
    try {
      const { data } = await orgzService.get_delivery_addresses(id);

      this.mutations.SET_DELIVERY_ADDRESSES(data);
    } catch (error) {
      console.log(error);
    }
  }

  async get_delivary_template(doc: DocsTemplates) {
    try {
      const { data: { path } } = await new DocsTemplatesService().get_document_template(doc);

      return path;
    } catch (error) {
      console.log(error);
    }
  }

  async add_delivery_address(address: DeliveryAddress) {
    try {
      const id = this.state.current_organization?.id;

      if (typeof id !== 'undefined') {
        const { code = ResponseStatuses.Success.CREATED } = await orgzService.add_delivery_address(id, address);

        if (code === ResponseStatuses.Success.CREATED) {
          this.mutations.ADD_DELIVERY_ADDRESSES(address);
        }
        return code;
      }
    } catch (error) {
      console.log(error);
    }
  }

  async get_transport_companies(id: string | number) {
    try {
      const { data } = await orgzService.get_transport_companies(id);

      this.mutations.SET_TRANSPORT_COMPANIES(data);
    } catch (error) {
      console.log(error);
    }
  }

  async get_company_contracts_accounts(id: string | number) {
    try {
      const payload: {filters:ModelContractsFilter} = {
        filters: this.state.filters_active_contracts,
      };
      const { data } = await orgzService.get_company_contracts_accounts(id, payload);

      this.mutations.SET_ACCOUNTS(data.invoices);
      this.mutations.SET_CONTRACTS(data.contracts);
      this.mutations.SET_CONTRACTS_ACTIVE_COUNT(data.active_contract_count);
    } catch (error) {
      console.log(error);
    }
  }

  async get_partner_sale_points() {
    try {
      const { data } = await orgzService.get_partner_sale_points();

      this.mutations.SET_SALES_POINTS(data);
    } catch (error) {
      console.error(error);
    }
  }

  async add_new_sale_point(point: Organizations.SalePoint) {
    try {
      const { code = ResponseStatuses.Success.CREATED, data } = await orgzService.add_new_sale_point(point);

      this.mutations.ADD_SALE_POINT({
        ...point,
        id: data.sale_point_id,
      });

      return code;
    } catch (error) {
      console.log(error);
    }
  }

  async del_sale_point(id: string | number) {
    try {
      const { code = ResponseStatuses.Success.OK } = await orgzService.del_sale_point(id);

      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async upd_sale_point(point: Organizations.SalePoint) {
    try {
      const { code = ResponseStatuses.Success.UPDATED } = await orgzService.upd_sale_point(point);

      return code;
    } catch (error) {
      console.error(error);
      return (error as AxiosResponse).status;
    }
  }

  async get_api_token_key() {
    try {
      const data = await orgzService.generate_api_token_key();

      this.mutations.SET_API_TOKEN_KEY(data);
    } catch (error: any) {
      console.error(error);

      if (error.data.code === ResponseStatuses.Fail.TOO_MANY_REQUESTS && error.data.message) {
        this.mutations.SET_API_KEY_MESSAGE(error.data.message);
        this.mutations.SET_API_KEY_MESSAGE_TOGLE(true);
      }
    }
  }

  async change_webhook_url(url: string) {
    try {
      const { code } = await orgzService.change_webhook_url(url);

      return code;
    } catch (error) {
      console.error(error);
      this.mutations.SET_WEBHOOK_ERROR_MESSAGE((error as AxiosResponse).data.message);
      return (error as AxiosResponse).status;
    }
  }

  async get_webhook_logs(storage_limit?: number) {
    this.mutations.SET_WEBHOOK_LOGS_LOADING(true);

    try {
      const {
        page = 1, itemsPerPage = 10,
      } = this.state.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: {},
        pagination: {
          limit: storage_limit ?? itemsPerPage,
          page,
        },
      };

      const { data, meta } = await orgzService.get_webhook_logs(payload);

      this.mutations.SET_WEBHOOK_LOGS(data.items);
      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.SET_WEBHOOK_LOGS_LOADING(false);
    }
  }

  async getWebhookObjects() {
    const response = await orgzService.getWebhookObjects();

    this.mutations.SET_WEBHOOK_OBJECTS(response.data);
  }

  async get_contract_by_id(contract_id: number | string) {
    try {
      const { data } = await orgzService.get_contract_by_id(contract_id);

      this.mutations.SET_CONTRACT(data);
    } catch (error) {
      console.error(error);
    }
  }
}
